import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  corruptedFile,
  eye,
  onboardEmpty,
  ongoingIc,
  pendingIcon,
} from '../../assets/images/images';
import {
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  TableLayout,
} from '../../components';
import {Constants} from '../../helpers';
import * as Actions from '../../store/actions';
import moment from 'moment';
import {PageContext} from '../../helpers/context';
import EmptyState from '../../components/EmptyState';
import VerificationWrapper from './index';
import {getStatusTextAndStyles} from '../../helpers/getStatusTextAndStyles';
import {useWidgetFlows} from '../../hooks/useWidgetFlows';
import {useAllApps} from '../../hooks/useApps';
import {getParams, handleSetIds, NoSearchResults} from './utils';
import {OverviewAnalytics} from './OverviewAnalytics';
import {shortenLargeNumbers} from '../../helpers/shortenLargeNumbers';
import PageNumber from 'components/PageNumber';

function PendingVerifications() {
  const navigate = useNavigate();
  const {widgetFlows, isLoading} = useWidgetFlows();
  const dispatch = useDispatch();
  const {
    verifications: {verifications, loading},
  } = useSelector(state => ({
    verifications: state?.verifications,
    auth: state?.auth,
  }));
  const getFlowName = useCallback(
    flowId => widgetFlows?.find(flow => flow.value === flowId)?.name,
    [widgetFlows],
  );

  // search query implementation
  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const search = query.get('search');
  const [searchText, setSearchText] = useState(search);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const {userApps, appIds, isLoading: appsLoading} = useAllApps();

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('verifications');
  }, [setPageName]);
  const pageClick = selected => {
    setCurrentPage(selected);
  };

  useEffect(() => {
    const apiParams = getParams({
      limit,
      appIds,
      currentPage,
      setCurrentPage,
      setSearchText,
      setFiltersApplied,
    });

    !appsLoading &&
      userApps &&
      appIds &&
      dispatch(Actions?.getVerifications({...apiParams, pending: true}));
  }, [appIds, appsLoading, currentPage, dispatch, limit, userApps]);

  const verificationsData = useMemo(
    () => (verifications ? verifications?.data : []),
    [verifications],
  );
  const handleResetFilter = () => {
    navigate('');
    setCurrentPage(1);

    dispatch(
      Actions?.getVerifications({
        page: 1,
        limit,
        appIds: appIds,
        pending: true,
      }),
    );
    setSearchText('');
  };

  const getVerificationsWithSearch = (text, appIds) => {
    const apiParams = {
      page: 1,
      limit,
      term: text,
      appIds,
      pending: true,
    };

    setCurrentPage(1);
    dispatch(Actions?.getVerifications(apiParams));
  };
  const clearSearch = () => {
    setSearchText('');
    setCurrentPage(1);
    navigate('');
    getVerificationsWithSearch('', appIds);
  };

  const cardData = [
    {
      icon: pendingIcon,
      alt: 'pending',
      title: 'Pending',
      value: shortenLargeNumbers(verifications?.meta?.total_count || 0, null, {
        startAt: 'M',
        decimals: 0,
      }),
    },
  ];
  useEffect(() => {
    localStorage.setItem(
      'pendingCount',
      JSON.stringify(verifications?.meta?.total_count) || '0',
    );
  }, [verifications?.meta?.total_count]);

  return (
    <VerificationWrapper
      isChild={true}
      count={verifications?.meta?.total_count}
    >
      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SearchInput
          onChange={text => {
            setSearchText(text);
            if (text) {
              getVerificationsWithSearch(text, appIds);
              navigate(`?search=${text}`);
            } else {
              clearSearch();
            }
          }}
          defaultValue={searchText}
          placeholder="Search by name, or, reference ID"
        />
      </div>

      <OverviewAnalytics cardData={cardData} isLoading={loading || isLoading} />

      <TableLayout negativeMargins>
        <thead className="font-semibold uppercase">
          <tr className="bg-white80">
            <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-center gap-1">
                <span>name</span>
              </div>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-center gap-1">
                <span>Flows</span>
              </div>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-center gap-1">
                <span>Reference ID</span>
              </div>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-center gap-1">
                <span>status</span>
              </div>
            </th>

            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <div className="flex items-center gap-1">
                <span>Date/Time created</span>
              </div>
            </th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td colSpan={8}>
              {loading || isLoading ? (
                <Loader height={45} />
              ) : !loading && !verificationsData?.length && search !== null ? (
                !filtersApplied ? (
                  <EmptyState
                    height={45}
                    body="No data found"
                    src={corruptedFile}
                    noBtn
                  />
                ) : (
                  <EmptyState
                    height={45}
                    body="No results found"
                    noBtn
                    customBtn={
                      <button
                        onClick={handleResetFilter}
                        className="text-brandBlue p-4 text-sm font-medium"
                      >
                        Update preferences
                      </button>
                    }
                  />
                )
              ) : !loading &&
                !verificationsData.length &&
                search === null &&
                (!verifications || !appIds) ? (
                // eslint-disable-next-line react/jsx-indent
                <EmptyState
                  body="Identify your users by viewing their verification details"
                  src={onboardEmpty}
                  buttonText="Go to Verification FLow"
                  onClick={() => navigate('/easy-onboard')}
                  height={45}
                />
              ) : !verificationsData.length && search !== null ? (
                <NoSearchResults />
              ) : null}
            </td>
          </tr>
        </tbody>

        <tbody className="text-sm bg-white">
          {!(loading || isLoading) &&
            verificationsData &&
            verificationsData.map((verification, i) => (
              <tr
                className="border-[0.5px] border-[#E4E3EC] cursor-pointer"
                key={i}
                onClick={() => {
                  navigate(
                    Constants.PAGES_URL.VERIFICATIONS_BIO_DATA.replace(
                      ':slug',
                      verification?.verification_id,
                    ),
                    {
                      state: {
                        sessionId: verification?.verification_id,
                      },
                    },
                  );
                  handleSetIds(verification);
                }}
              >
                <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                  {verification.first_name
                    ? `${verification.first_name} ${verification.last_name} ${verification.middle_name}`
                    : verification.business_name
                    ? verification.business_name
                    : '-'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {getFlowName(verification?.flow_id) ||
                    verification?.flow_id ||
                    '-'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {verification?.reference_id}
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  <div
                    className={`uppercase pt-[5px] pb-[3px] px-2 text-xs font-medium   rounded-full w-fit flex items-start gap-[2px] ${
                      getStatusTextAndStyles(
                        verification?.status?.toLowerCase(),
                      ).styles
                    }`}
                  >
                    {
                      getStatusTextAndStyles(
                        verification?.status?.toLowerCase(),
                      ).text
                    }
                    {verification?.status?.toLowerCase() === 'ongoing' && (
                      <img src={ongoingIc} alt="" />
                    )}
                    {/* <img src={expiredIc} alt="" /> */}
                  </div>
                </td>{' '}
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {moment(verification?.datetime).format('Do MMM, YYYY h:mm A')}
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  <div className="flex items-center gap-4">
                    <button title="View" className="h-4 aspect-square">
                      <img src={eye} alt="" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </TableLayout>

      {!loading && verificationsData?.length !== 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={+currentPage}
            limit={limit}
            totalCount={verifications?.meta?.total_count}
          />

          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={setLimit}
              total={verifications && verifications?.meta?.total_count}
              limit={verificationsData && verificationsData?.length}
            />

            <Pagination
              total={
                verifications &&
                Math.ceil(verifications?.meta?.total_count / limit)
              }
              current={+currentPage}
              onPageChange={activePage => {
                pageClick(activePage);
                const searchParams = new URLSearchParams(
                  document.location.search,
                );
                searchParams.set('page', activePage);
                const newSearchParams = searchParams.toString();
                navigate(`?${newSearchParams}`);
              }}
            />
          </div>
        </div>
      )}
    </VerificationWrapper>
  );
}

export default PendingVerifications;
